import { AnonymousCredential, BlobServiceClient, BlockBlobClient } from '@azure/storage-blob';
import { QueryFunction } from 'react-query';
import { transliterate, slugify } from 'transliteration';
import { v4 as uuid } from 'uuid';

import { UploadedFile } from 'pkg/components/FileUploader/UploadedFile';
import { GetFunction, httpClient, PostFunction } from 'pkg/httpClient/httpClient';

const UPLOAD_FILE_URL = '/quote/uploadFile';
const REJECT_FILE_URL = '/quote/rejectFiles';
const ALLOWED_FILES_URL = '/fileTypes';
const QUOTE_FILES_URL = '/quote/files';

export class FileUploadError extends Error {}

interface FilesRejection {
  quoteId?: string;
  name?: string;
  email?: string;
  serviceName: string;
  fileExtensions: string[];
}
interface FileManager extends Pick<BlockBlobClient, 'uploadData' | 'delete'> {}
interface FileUploaderService {
  queryKey: string;
  getAllowedFileTypes: () => Promise<string[]>;
  getFilesByQuote: QueryFunction<string[], [string, string]>;
  updateUploadedFiles: (id: string) => (files: string[]) => Promise<void>;
  uploadFiles: (filesWithCallback: { file: File; onProgress: (file: UploadedFile) => void }[]) => Promise<void>;
  deleteFile: (file: UploadedFile) => Promise<void>;
  sendRejectionMessage: (data: FilesRejection) => Promise<void>;
}

const getBlockBlobClient = (url: string, fileName: string): FileManager => {
  const blobServiceClient = new BlobServiceClient(
    `${process.env.REACT_APP_BLOB_STORAGE_URL}${url}`,
    new AnonymousCredential()
  );
  const container = blobServiceClient.getContainerClient(String(process.env.REACT_APP_BLOB_STORAGE_CONTAINER));
  return container.getBlockBlobClient(fileName);
};

export const getExtensionsByFileNames = (rejectedFiles: string[]): string[] => {
  const extensions = new Set(rejectedFiles.map((fileName) => fileName.split('.').pop()));
  return Array.from(extensions) as string[];
};

const fileUploaderServiceFactory = (
  http: { get: GetFunction; post: PostFunction; put: PostFunction },
  fileManagerFactory: (url: string, fileName: string) => FileManager
): FileUploaderService => ({
  queryKey: 'allowedTypes',
  getFilesByQuote: ({ queryKey }) =>
    http.get<{ files: string[] }>(`${QUOTE_FILES_URL}/${queryKey[1]}`).then(({ data }) => data.files),
  updateUploadedFiles: (id) => async (files) => {
    await http.put<{ files: string[] }, void>(`${QUOTE_FILES_URL}/${id}`, { files });
  },
  getAllowedFileTypes: () => http.get<string[]>(ALLOWED_FILES_URL).then(({ data }) => data),
  uploadFiles: async (filesWithCallback) => {
    await Promise.all(
      filesWithCallback.map(async ({ file, onProgress }) => {
        const controller = new AbortController();
        const id = uuid();
        const fileWithUpdatedName = new File([file], `${id}_${slugify(transliterate(file.name))}`, {
          type: file.type,
          lastModified: file.lastModified,
        });
        onProgress({ file: fileWithUpdatedName, id, progress: 0, isUploaded: false, controller });

        const { data: url } = await http.get<string>(UPLOAD_FILE_URL);

        const handleOnProgress = ({ loadedBytes }: { loadedBytes: number }) => {
          onProgress({
            file: fileWithUpdatedName,
            isUploaded: loadedBytes === fileWithUpdatedName.size,
            progress: (loadedBytes / fileWithUpdatedName.size) * 100,
            id,
            sasUrl: url,
            controller,
          });
        };

        const fileManager = fileManagerFactory(url, fileWithUpdatedName.name);
        return fileManager
          .uploadData(fileWithUpdatedName, {
            onProgress: handleOnProgress,
            abortSignal: controller.signal,
          })
          .catch((error: Error & { code: string }) => {
            if (error.name !== 'AbortError') {
              throw new FileUploadError(`${error.name} with code (${error.code})`);
            }
          });
      })
    );
  },
  deleteFile: async ({ file, sasUrl, isUploaded, controller }) => {
    if (isUploaded && sasUrl) {
      try {
        await fileManagerFactory(sasUrl, file.name).delete();
      } catch (error) {
        throw new FileUploadError((error as Error).message);
      }
    } else {
      controller.abort();
    }
  },
  sendRejectionMessage: async (data) => {
    await http.post(REJECT_FILE_URL, data);
  },
});

const fileUploaderService = fileUploaderServiceFactory(httpClient, getBlockBlobClient);

export { fileUploaderService, fileUploaderServiceFactory };
